import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api, money, formatApiError } from "@/lib/api";
import { useBusiness } from "@/context/BusinessContext";
import { useSettings } from "@/context/SettingsContext";
import { useCart } from "@/context/CartContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Printer,
  ArrowLeft,
  Search,
  Minus,
  Plus,
  ShoppingBag,
  ChevronUp,
  ChevronDown,
  Trash2,
  CreditCard,
} from "lucide-react";
import { toast } from "sonner";

/**
 * Words-only quick menu — single column, no images, print-ready, with inline
 * order controls (+/-) on every row and a sticky totals pill that expands
 * into a full pre-pay checkout panel.
 */
export default function QuickMenuPage() {
  const { business } = useBusiness();
  const settings = useSettings();
  const { lines, addLine, updateQuantity, totalItems, totalAmount } = useCart();
  const [categories, setCategories] = useState([]);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const [catRes, itemRes] = await Promise.all([
          api.get("/categories"),
          api.get("/menu", { params: { available_only: true } }),
        ]);
        setCategories(catRes.data);
        setItems(itemRes.data);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  // For each item, find a SIMPLE cart line (no addons) and return its qty + index.
  const lineByItem = useMemo(() => {
    const map = new Map();
    lines.forEach((l, idx) => {
      if (!l.item_id) return;
      if ((l.selected_addons || []).length > 0) return;
      // First simple line wins; later ones (if any) are ignored — clicking +/-
      // adjusts the existing line.
      if (!map.has(l.item_id)) map.set(l.item_id, { qty: l.quantity, index: idx });
    });
    return map;
  }, [lines]);

  function unitPriceFor(item, category) {
    const cols = category?.price_columns?.length
      ? category.price_columns
      : [{ key: "default", label: "" }];
    const first = cols[0];
    const raw = (item.prices || {})[first.key];
    const v = raw != null ? raw : first.key === "default" ? item.price : null;
    return Number(v) || 0;
  }

  function inc(item, category) {
    if (item.is_priced === false) return; // banquet-included rows can't be ordered alone
    const existing = lineByItem.get(item.id);
    if (existing) {
      updateQuantity(existing.index, existing.qty + 1);
    } else {
      const cols = category?.price_columns?.length
        ? category.price_columns
        : [{ key: "default", label: "" }];
      const priceColumn = cols[0].key;
      addLine({
        item_id: item.id,
        name: item.name,
        base_price: unitPriceFor(item, category),
        quantity: 1,
        selected_addons: [],
        notes: "",
        calories: Number(item.calories) || 0,
        tags: item.tags || [],
        price_column: priceColumn,
        price_label: cols[0].label || "",
      });
    }
  }

  function dec(item) {
    const existing = lineByItem.get(item.id);
    if (!existing) return;
    updateQuantity(existing.index, existing.qty - 1); // CartContext drops the line at 0
  }

  const grouped = useMemo(() => {
    const search = q.trim().toLowerCase();
    const filterFn = (it) =>
      !search ||
      `${it.name} ${it.description || ""}`.toLowerCase().includes(search);
    const out = [];
    for (const c of categories) {
      const list = items.filter((it) => it.category_id === c.id).filter(filterFn);
      if (list.length === 0) continue;
      out.push({ category: c, items: list });
    }
    return out;
  }, [categories, items, q]);

  const prepayPct = Math.max(
    0,
    Math.min(50, Number(settings?.prepay_discount_percent || 0)),
  );
  const discountAmount = +(totalAmount * (prepayPct / 100)).toFixed(2);
  const payNowTotal = +(totalAmount - discountAmount).toFixed(2);

  function printPage() {
    window.print();
  }

  return (
    <div data-testid="quick-menu-page" className="bg-white min-h-screen quick-menu-root pb-32">
      {/* Print stylesheet — hides header/footer/controls so the menu prints cleanly */}
      <style>{`
        @media print {
          @page { size: A4; margin: 16mm 14mm; }
          .quick-menu-controls, .site-header, [data-testid="site-header"], [data-testid="site-footer"], [data-testid="quick-order-bar"] {
            display: none !important;
          }
          .quick-menu-root, html, body { background: white !important; color: #000 !important; }
          .quick-menu-page-break { break-inside: avoid; page-break-inside: avoid; }
          body { font-size: 10.5pt; }
          h1 { font-size: 24pt !important; }
          h2 { font-size: 14pt !important; }
        }
      `}</style>

      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-10 md:py-14">
        <div className="quick-menu-controls flex items-center justify-between gap-3 flex-wrap mb-6">
          <Link to="/menu" data-testid="quick-menu-back" className="inline-flex items-center text-sm text-[#5C625E] hover:text-[#C05746]">
            <ArrowLeft className="h-4 w-4 mr-1.5" strokeWidth={1.5} /> Full menu
          </Link>
          <div className="flex gap-2">
            <Link to="/menu/specials" data-testid="quick-menu-specials-link">
              <Button variant="outline" className="rounded-full">
                <Printer className="h-4 w-4 mr-1.5" strokeWidth={1.5} /> Chef&apos;s Specials A5
              </Button>
            </Link>
            <Button
              data-testid="quick-menu-print"
              onClick={printPage}
              variant="outline"
              className="rounded-full"
            >
              <Printer className="h-4 w-4 mr-1.5" strokeWidth={1.5} /> Print full menu
            </Button>
          </div>
        </div>

        <header className="text-center pb-6 border-b-2 border-[#121614]">
          <p className="text-[10px] uppercase tracking-[0.35em] font-bold text-[#5A7247]">
            Quick menu
          </p>
          <h1 className="font-serif-display text-4xl sm:text-5xl mt-2 text-[#121614]">
            {business?.business_name || "Menu"}
          </h1>
          {business?.tagline && (
            <p className="mt-2 text-sm italic text-[#5C625E]">{business.tagline}</p>
          )}
          {prepayPct > 0 && (
            <p
              data-testid="quick-menu-prepay-banner"
              className="mt-3 inline-block bg-[#5A7247]/10 text-[#2C3A22] text-xs uppercase tracking-wider font-bold px-3 py-1 rounded-full"
            >
              Pre-pay online and save {prepayPct}%
            </p>
          )}
        </header>

        <div className="quick-menu-controls mt-6 relative">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-[#5C625E]" strokeWidth={1.5} />
          <Input
            data-testid="quick-menu-search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Filter dishes…"
            className="pl-11 h-11 rounded-full bg-[#F3F0EA] border-none"
          />
        </div>

        {loading && (
          <p data-testid="quick-menu-loading" className="mt-10 text-center text-[#5C625E]">
            Loading menu…
          </p>
        )}

        {!loading && grouped.length === 0 && (
          <p data-testid="quick-menu-empty" className="mt-10 text-center text-[#5C625E]">
            No dishes match "{q}".
          </p>
        )}

        <div className="mt-8 space-y-10">
          {grouped.map((g) => (
            <section
              key={g.category.id}
              data-testid={`quick-section-${g.category.slug}`}
              className="quick-menu-page-break"
            >
              <h2 className="font-serif-display text-2xl sm:text-3xl text-[#121614] mb-3">
                {g.category.name}
              </h2>
              {g.category.kind && (
                <p className="text-[10px] uppercase tracking-[0.25em] font-bold text-[#5A7247] mb-3">
                  {g.category.kind}
                </p>
              )}
              <ul className="divide-y divide-[#E8E5DF]">
                {g.items.map((it) => (
                  <Row
                    key={it.id}
                    item={it}
                    category={g.category}
                    qty={lineByItem.get(it.id)?.qty || 0}
                    onInc={() => inc(it, g.category)}
                    onDec={() => dec(it)}
                  />
                ))}
              </ul>
            </section>
          ))}
        </div>

        {!loading && grouped.length > 0 && (
          <p className="mt-12 text-[11px] text-[#5C625E] text-center quick-menu-controls">
            Prices include GST. Allergens are approximations — please ask staff for severe allergies.
          </p>
        )}
      </div>

      <QuickOrderBar
        totalItems={totalItems}
        subtotal={totalAmount}
        prepayPct={prepayPct}
        discountAmount={discountAmount}
        payNowTotal={payNowTotal}
      />
    </div>
  );
}

function Row({ item, category, qty, onInc, onDec }) {
  const cols = category?.price_columns?.length
    ? category.price_columns
    : [{ key: "default", label: "" }];
  const isWhole = category?.price_format === "whole";
  const format = (v) =>
    v == null || v === ""
      ? "—"
      : isWhole
        ? `$${Math.round(Number(v))}`
        : `$${Number(v).toFixed(2)}`;
  const banquet = item.is_priced === false;

  return (
    <li data-testid={`quick-row-${item.id}`} className="py-3">
      <div className="flex items-baseline gap-2">
        <p className="font-medium text-[#121614]">{item.name}</p>
        {(item.tags || []).includes("chef_special") && (
          <span className="text-[10px] uppercase tracking-wider font-bold text-[#C05746]">
            ★ Special
          </span>
        )}
        <span className="flex-1 border-b border-dotted border-[#E8E5DF] mx-2 mb-1" />
        <span className="font-medium text-[#121614] whitespace-nowrap text-sm">
          {banquet ? (
            <span className="text-[#5A7247] text-xs uppercase tracking-wider">included</span>
          ) : cols.length > 1 ? (
            cols
              .map((c) => {
                const raw = (item.prices || {})[c.key];
                const v = raw != null ? raw : c.key === "default" ? item.price : null;
                if (v == null || v === "") return null;
                return `${c.label ? c.label + " " : ""}${format(v)}`;
              })
              .filter(Boolean)
              .join("  ·  ")
          ) : (
            format((item.prices || {}).default ?? item.price)
          )}
        </span>
      </div>
      {item.description && (
        <p className="text-xs text-[#5C625E] mt-1 leading-relaxed pr-12">
          {item.description}
        </p>
      )}
      {!banquet && (
        <div
          data-testid={`quick-row-controls-${item.id}`}
          className="quick-menu-controls mt-2 flex items-center gap-2"
        >
          <button
            type="button"
            data-testid={`quick-row-dec-${item.id}`}
            onClick={onDec}
            disabled={qty === 0}
            aria-label={`Remove one ${item.name}`}
            className="h-8 w-8 rounded-full border border-[#E8E5DF] inline-flex items-center justify-center text-[#5C625E] hover:text-[#C05746] hover:border-[#C05746] disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
          >
            <Minus className="h-3.5 w-3.5" strokeWidth={1.5} />
          </button>
          <span
            data-testid={`quick-row-qty-${item.id}`}
            className={`min-w-[24px] text-center text-sm font-medium ${
              qty > 0 ? "text-[#121614]" : "text-[#5C625E]/60"
            }`}
          >
            {qty}
          </span>
          <button
            type="button"
            data-testid={`quick-row-inc-${item.id}`}
            onClick={onInc}
            aria-label={`Add one ${item.name}`}
            className="h-8 w-8 rounded-full border border-[#E8E5DF] inline-flex items-center justify-center text-[#5C625E] hover:text-white hover:bg-[#C05746] hover:border-[#C05746] transition-colors"
          >
            <Plus className="h-3.5 w-3.5" strokeWidth={1.5} />
          </button>
        </div>
      )}
    </li>
  );
}

/* ----------------------------------------------------------------------------
 * QuickOrderBar — sticky bottom pill that expands into a pre-pay checkout panel.
 * -------------------------------------------------------------------------- */
function QuickOrderBar({ totalItems, subtotal, prepayPct, discountAmount, payNowTotal }) {
  const { lines, updateQuantity, clear } = useCart();
  const [expanded, setExpanded] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    customer_name: "",
    customer_email: "",
    customer_phone: "",
    pickup_date: "",
    pickup_time: "",
    notes: "",
  });

  function set(k, v) {
    setForm((f) => ({ ...f, [k]: v }));
  }

  async function payNow() {
    if (!form.customer_name.trim() || !form.customer_email.trim() || !form.customer_phone.trim()) {
      toast.error("Name, email and phone are required to pre-pay");
      return;
    }
    if (!form.pickup_date || !form.pickup_time) {
      toast.error("Pick a pickup date and time");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        customer_name: form.customer_name,
        customer_email: form.customer_email,
        customer_phone: form.customer_phone,
        pickup_date: form.pickup_date,
        pickup_time: form.pickup_time,
        notes: form.notes,
        items: lines.map((l) => ({
          item_id: l.item_id,
          quantity: l.quantity,
          selected_addons: l.selected_addons || [],
          notes: l.notes || "",
          price_column: l.price_column || "",
        })),
        payment_method: "stripe",
        split_payment: "none",
        seats: 1,
        seat_assignments: [],
        origin_url: window.location.origin,
      };
      const { data } = await api.post("/orders", payload);
      if (data.checkout_url) {
        clear();
        window.location.href = data.checkout_url;
      } else {
        toast.error("Stripe could not start a checkout session");
      }
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setSubmitting(false);
    }
  }

  if (totalItems === 0) return null;

  return (
    <div
      data-testid="quick-order-bar"
      className="fixed bottom-3 sm:bottom-6 inset-x-3 sm:inset-x-auto sm:right-6 z-40 print:hidden"
    >
      <div
        className={`bg-[#121614] text-[#FDFBF7] rounded-2xl shadow-2xl border border-white/10 transition-all overflow-hidden ${
          expanded ? "w-full sm:w-[420px]" : "sm:w-[320px]"
        }`}
      >
        {/* Collapsed pill — always visible */}
        <button
          type="button"
          data-testid="quick-order-toggle"
          onClick={() => setExpanded((v) => !v)}
          className="w-full flex items-center gap-3 px-4 py-3 text-left"
          aria-expanded={expanded}
          aria-label="Toggle order details"
        >
          <div className="h-9 w-9 rounded-full bg-[#C05746] inline-flex items-center justify-center shrink-0">
            <ShoppingBag className="h-4 w-4" strokeWidth={1.75} />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-[10px] uppercase tracking-[0.2em] text-white/60">
              Your order
            </p>
            <p
              data-testid="quick-order-summary"
              className="font-medium leading-tight"
            >
              <span data-testid="quick-order-count">{totalItems}</span> item
              {totalItems === 1 ? "" : "s"} ·{" "}
              <span data-testid="quick-order-subtotal">{money(subtotal)}</span>
              {prepayPct > 0 && (
                <span
                  data-testid="quick-order-prepay-tease"
                  className="ml-1 text-[#E8B4A2] text-xs"
                >
                  → {money(payNowTotal)} prepay
                </span>
              )}
            </p>
          </div>
          {expanded ? (
            <ChevronDown className="h-5 w-5 text-white/70 shrink-0" strokeWidth={1.5} />
          ) : (
            <ChevronUp className="h-5 w-5 text-white/70 shrink-0" strokeWidth={1.5} />
          )}
        </button>

        {expanded && (
          <div
            data-testid="quick-order-panel"
            className="px-4 pb-4 max-h-[70vh] overflow-y-auto"
          >
            <ul className="divide-y divide-white/10">
              {lines.map((l, idx) => (
                <li
                  key={idx}
                  data-testid={`quick-order-line-${idx}`}
                  className="py-2 flex items-center gap-2"
                >
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">{l.name}</p>
                    <p className="text-[11px] text-white/60">
                      {money(l.base_price)} each
                    </p>
                  </div>
                  <div className="inline-flex items-center gap-1">
                    <button
                      type="button"
                      data-testid={`quick-order-line-dec-${idx}`}
                      onClick={() => updateQuantity(idx, l.quantity - 1)}
                      className="h-6 w-6 rounded-full bg-white/10 hover:bg-white/20 inline-flex items-center justify-center"
                      aria-label="Decrease"
                    >
                      <Minus className="h-3 w-3" strokeWidth={1.5} />
                    </button>
                    <span className="min-w-[18px] text-center text-sm">
                      {l.quantity}
                    </span>
                    <button
                      type="button"
                      data-testid={`quick-order-line-inc-${idx}`}
                      onClick={() => updateQuantity(idx, l.quantity + 1)}
                      className="h-6 w-6 rounded-full bg-white/10 hover:bg-white/20 inline-flex items-center justify-center"
                      aria-label="Increase"
                    >
                      <Plus className="h-3 w-3" strokeWidth={1.5} />
                    </button>
                  </div>
                  <span className="w-16 text-right text-sm font-medium">
                    {money(l.line_total)}
                  </span>
                  <button
                    type="button"
                    data-testid={`quick-order-line-remove-${idx}`}
                    onClick={() => updateQuantity(idx, 0)}
                    className="h-6 w-6 rounded-full text-white/60 hover:text-red-300 inline-flex items-center justify-center"
                    aria-label="Remove"
                  >
                    <Trash2 className="h-3 w-3" strokeWidth={1.5} />
                  </button>
                </li>
              ))}
            </ul>

            <dl className="mt-3 space-y-1 text-sm border-t border-white/10 pt-3">
              <div className="flex justify-between">
                <dt className="text-white/70">Subtotal</dt>
                <dd>{money(subtotal)}</dd>
              </div>
              {prepayPct > 0 && (
                <div
                  data-testid="quick-order-discount-row"
                  className="flex justify-between text-[#E8B4A2]"
                >
                  <dt>Prepay discount ({prepayPct}%)</dt>
                  <dd>− {money(discountAmount)}</dd>
                </div>
              )}
              <div className="flex justify-between text-base font-semibold pt-1 border-t border-white/10">
                <dt>Pay now</dt>
                <dd data-testid="quick-order-pay-total">{money(payNowTotal)}</dd>
              </div>
            </dl>

            <div className="mt-4 space-y-2">
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label className="text-[10px] uppercase tracking-wider text-white/60">Name</Label>
                  <Input
                    data-testid="quick-order-name"
                    value={form.customer_name}
                    onChange={(e) => set("customer_name", e.target.value)}
                    className="mt-1 h-9 rounded-lg bg-white/10 border-white/10 text-white placeholder:text-white/40"
                    placeholder="Full name"
                  />
                </div>
                <div>
                  <Label className="text-[10px] uppercase tracking-wider text-white/60">Phone</Label>
                  <Input
                    data-testid="quick-order-phone"
                    value={form.customer_phone}
                    onChange={(e) => set("customer_phone", e.target.value)}
                    className="mt-1 h-9 rounded-lg bg-white/10 border-white/10 text-white placeholder:text-white/40"
                    placeholder="04xx…"
                  />
                </div>
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-wider text-white/60">Email</Label>
                <Input
                  data-testid="quick-order-email"
                  type="email"
                  value={form.customer_email}
                  onChange={(e) => set("customer_email", e.target.value)}
                  className="mt-1 h-9 rounded-lg bg-white/10 border-white/10 text-white placeholder:text-white/40"
                  placeholder="you@example.com"
                />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label className="text-[10px] uppercase tracking-wider text-white/60">Pickup date</Label>
                  <Input
                    data-testid="quick-order-pickup-date"
                    type="date"
                    value={form.pickup_date}
                    onChange={(e) => set("pickup_date", e.target.value)}
                    className="mt-1 h-9 rounded-lg bg-white/10 border-white/10 text-white"
                  />
                </div>
                <div>
                  <Label className="text-[10px] uppercase tracking-wider text-white/60">Pickup time</Label>
                  <Input
                    data-testid="quick-order-pickup-time"
                    type="time"
                    value={form.pickup_time}
                    onChange={(e) => set("pickup_time", e.target.value)}
                    className="mt-1 h-9 rounded-lg bg-white/10 border-white/10 text-white"
                  />
                </div>
              </div>
              <Button
                data-testid="quick-order-pay-button"
                onClick={payNow}
                disabled={submitting}
                className="w-full mt-2 bg-[#C05746] hover:bg-[#A64A3C] text-white rounded-full h-11"
              >
                <CreditCard className="h-4 w-4 mr-1.5" strokeWidth={1.5} />
                {submitting
                  ? "Starting Stripe…"
                  : prepayPct > 0
                    ? `Pre-pay ${money(payNowTotal)} & save ${prepayPct}%`
                    : `Pay ${money(payNowTotal)}`}
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
